import GridTable from "@components/grid-table"
import { useEmailLogsPageContext } from "@contexts/main/email_logs_page_context"
import { listEmailAdminLogsQuery } from "@domain/email_logs"
import SearchIcon from "@mui/icons-material/Search"
import { Box, InputAdornment, Stack, TextField } from "@mui/material"
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams
} from "@mui/x-data-grid"
import { useQuery } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import { formatDate } from "@utils/functions/formatDate"
import { showErrorToast } from "@utils/toast"
import {
  EmailAdminLogsData,
  GetEmailAdminLogsInput
} from "__generated__/graphql"
import { useEffect, useState } from "react"

interface SanitizedEmailLog {
  _id: string
  customerName: string
  provider: string
  status: string
  from: string
  to: string
  subject: string
  mailType: string
  errorMessage: string
  createdAt: Date
  expiresAt: Date
  updatedAt: Date
}

interface SanitizedEmailLogsResponse {
  logs: SanitizedEmailLog[]
  total: number
}

const getFirstRecipient = (recipients?: (string | null)[] | null) => {
  const firstRecipient = recipients?.find((recipient): recipient is string => {
    return Boolean(recipient)
  })

  return firstRecipient ?? ""
}

export default function EmailLogList() {
  const { translate, filters } = useEmailLogsPageContext()
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100
  })
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 500)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }))
  }, [debouncedSearch])

  const { data, isLoading } = useQuery({
    queryKey: [
      "email-logs-list",
      pagination.page,
      pagination.pageSize,
      filters,
      debouncedSearch
    ],
    queryFn: async () => {
      const params: GetEmailAdminLogsInput = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        customerId: filters.customerId || "",
        startDate: filters.startDate ? filters.startDate.getTime() : undefined,
        endDate: filters.endDate ? filters.endDate.getTime() : undefined,
        search: debouncedSearch
      }
      const response = await listEmailAdminLogsQuery(params)
      if (!response.isSuccess || !response.data) {
        showErrorToast(
          response.errorMessage ??
            translate("error_fetching_logs") ??
            "Error fetching logs"
        )
        return { logs: [], total: 0 }
      }
      const sanitizedData = sanitizeEmailLogs(response.data)
      return sanitizedData
    },
    enabled: !!filters.customerId || !!filters.startDate || !!filters.endDate
  })

  console.log("data", data)

  const sanitizeEmailLogs = (
    rawData: EmailAdminLogsData
  ): SanitizedEmailLogsResponse => {
    return {
      logs: rawData.logs.map((log) => ({
        _id: log._id,
        customerName: log.customerName || "-",
        provider: log.provider || "-",
        status: log.status || "-",
        from: log.from || "-",
        to: getFirstRecipient(log.recipients?.to),
        subject: log.subject || "-", // Handling null subject
        mailType: log?.metadata?.mailType || "-",
        errorMessage: log.errorMessage || "-", // Handling null errorMessage
        createdAt: log.createdAt,
        expiresAt: log.expiresAt,
        updatedAt: log.updatedAt
      })),
      total: rawData.total
    }
  }

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: translate("created_at"),
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {formatDate(translate("date_format"), params.value as string)}
        </Box>
      )
    },
    {
      field: "customerName",
      headerName: translate("customer_name") || "Customer Name",
      flex: 1,
      minWidth: 150
    },
    {
      field: "provider",
      headerName: translate("provider") || "Provider",
      flex: 1,
      minWidth: 150
    },
    {
      field: "status",
      headerName: translate("status"),
      flex: 0.5,
      minWidth: 100
    },
    {
      field: "from",
      headerName: translate("from") || "From",
      flex: 0.7,
      minWidth: 180
    },
    {
      field: "to",
      headerName: translate("to") || "To",
      flex: 0.7,
      minWidth: 180
    },
    {
      field: "subject",
      headerName: translate("subject") || "Subject",
      flex: 0.7,
      minWidth: 120
    },
    {
      field: "mailType",
      headerName: translate("mail_type") || "Mail Type",
      flex: 1,
      minWidth: 200
    },
    {
      field: "errorMessage",
      headerName: "Error Message",
      flex: 1,
      minWidth: 150
    }
    /* {
      field: "actions",
      headerName: translate("actions") || "Actions",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onSelectEmail(params.row.emailSid as string)}
        >
          {translate("view") || "View"}
        </Button>
      )
    } */
  ]

  return (
    <Stack sx={{ height: "100%", p: 2, overflow: "hidden", gap: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={translate("search") || "Search..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            )
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              bgcolor: "background.paper"
            }
          }}
        />
      </Box>
      <GridTable
        loading={isLoading}
        rows={data?.logs || []}
        columns={columns}
        rowCount={data?.total || 0}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row) => row._id}
      />
    </Stack>
  )
}
